/** ERP-277: operator-only snapshots and explicitly confirmed, single-service probes. */
import { apiGet, apiPost, ApiError } from './apiClient.js';

const BASE = '/internal/admin/api/v1/ai/monitoring';

export interface AiMonitoringProbe {
  readonly operationId: string;
  readonly startedAt: string;
  readonly completedAt: string | null;
  readonly status: 'healthy' | 'degraded' | 'failed' | 'inconclusive' | 'running';
  readonly latencyMs: number | null;
  readonly httpStatus: number | null;
  readonly reasonCode: string | null;
  readonly usageReported: boolean;
  readonly inputTokens: string | null;
  readonly outputTokens: string | null;
}

export interface AiMonitoringService {
  readonly id: string;
  readonly kind: 'chat' | 'call_analysis' | 'stt' | 'embedding';
  readonly labelFa: string;
  readonly mode: 'remote' | 'local_fixture';
  readonly configuration: 'ready' | 'missing' | 'invalid' | 'disabled' | 'local';
  readonly providerCode: string | null;
  readonly requestedModel: string | null;
  readonly resolvedModel: string | null;
  readonly host: string | null;
  readonly reasonCode: string | null;
  readonly canProbe: boolean;
  readonly configFingerprint: string;
  readonly lastProbe: AiMonitoringProbe | null;
  readonly stale: boolean;
}

export interface AiMonitoringReport {
  readonly generatedAt: string;
  readonly staleAfterSeconds: number;
  readonly cooldownSeconds: number;
  readonly canManage: boolean;
  readonly services: readonly AiMonitoringService[];
}

export interface AiMonitoringProbeResult {
  readonly serviceId: string;
  readonly replayed: boolean;
  readonly probe: AiMonitoringProbe;
}

/** These calls deliberately retain ApiError.status (401 and 403 are distinct). */
export function getAiMonitoring(): Promise<AiMonitoringReport> {
  return apiGet<AiMonitoringReport>(BASE);
}

/** The caller retains the same operation key when retrying an uncertain request. */
export function probeAiMonitoring(
  input: { readonly serviceId: string; readonly configFingerprint: string },
  operationKey: string,
): Promise<AiMonitoringProbeResult> {
  return apiPost<AiMonitoringProbeResult>(`${BASE}/probe`, input, {
    headers: { 'idempotency-key': operationKey },
  });
}

export interface AiMonitoringStatus {
  readonly label: string;
  readonly tone: 'neutral' | 'success' | 'warn' | 'danger' | 'info';
}

export const AI_CONFIGURATION_STATUS: Readonly<
  Record<AiMonitoringService['configuration'], AiMonitoringStatus>
> = {
  ready: { label: 'تنظیمات آماده', tone: 'info' },
  missing: { label: 'تنظیمات ناقص', tone: 'danger' },
  invalid: { label: 'تنظیمات نامعتبر', tone: 'danger' },
  disabled: { label: 'غیرفعال', tone: 'warn' },
  local: { label: 'اجرای محلی', tone: 'neutral' },
};

export function isAiMonitoringSnapshotStale(report: AiMonitoringReport, now: number): boolean {
  const generated = Date.parse(report.generatedAt);
  return (
    !Number.isFinite(now) ||
    !Number.isFinite(generated) ||
    !Number.isFinite(report.staleAfterSeconds) ||
    report.staleAfterSeconds <= 0 ||
    now < generated ||
    now - generated >= report.staleAfterSeconds * 1000
  );
}

/** A configured endpoint is never itself evidence of a healthy remote response. */
export function aiMonitoringResponseStatus(
  service: AiMonitoringService,
  report: AiMonitoringReport,
  now: number,
): AiMonitoringStatus {
  if (service.mode === 'local_fixture' || service.configuration === 'local') {
    return { label: 'محلی؛ اتصال خارجی آزموده نشده', tone: 'neutral' };
  }
  if (service.configuration !== 'ready') {
    return { label: 'پاسخ معتبر برای تنظیمات فعلی نداریم', tone: 'warn' };
  }
  if (!service.canProbe) return { label: 'آزمون برای مسیر فعلی مجاز نیست', tone: 'warn' };
  const probe = service.lastProbe;
  if (!probe) return { label: 'هنوز آزمون نشده', tone: 'neutral' };
  if (service.stale || isAiMonitoringSnapshotStale(report, now))
    return { label: 'نتیجه قدیمی یا قابل اتکا نیست', tone: 'warn' };
  if (probe.status === 'running') return { label: 'آخرین وضعیت: در حال آزمون', tone: 'info' };
  const completed = probe.completedAt ? Date.parse(probe.completedAt) : NaN;
  if (
    !Number.isFinite(completed) ||
    now < completed ||
    now - completed >= report.staleAfterSeconds * 1000
  ) {
    return { label: 'نتیجه قدیمی یا قابل اتکا نیست', tone: 'warn' };
  }
  switch (probe.status) {
    case 'healthy':
      return { label: 'پاسخ سالم در آخرین آزمون', tone: 'success' };
    case 'degraded':
      return { label: 'پاسخ ناقص در آخرین آزمون', tone: 'warn' };
    case 'failed':
      return { label: 'آخرین آزمون ناموفق بود', tone: 'danger' };
    default:
      return { label: 'نتیجهٔ آزمون نامشخص است', tone: 'warn' };
  }
}

export function aiMonitoringCooldownSeconds(
  service: AiMonitoringService,
  report: AiMonitoringReport,
  now: number,
): number {
  const starts = [service, ...report.services]
    .map((item) => (item.lastProbe ? Date.parse(item.lastProbe.startedAt) : NaN))
    .filter(Number.isFinite);
  if (starts.length === 0 || !Number.isFinite(report.cooldownSeconds)) return 0;
  const started = Math.max(...starts);
  return Math.max(0, Math.ceil((started + report.cooldownSeconds * 1000 - now) / 1000));
}

export function aiMonitoringProbeBlockReason(
  service: AiMonitoringService,
  report: AiMonitoringReport,
  now: number,
): string | null {
  if (!report.canManage) return 'فقط مجوز مشاهده داری.';
  if (service.mode === 'local_fixture' || service.configuration === 'local')
    return 'این سرویس محلی است و آزمون اتصال خارجی ندارد.';
  if (service.configuration !== 'ready') return 'ابتدا تنظیمات این سرویس باید اصلاح یا فعال شود.';
  if (isAiMonitoringSnapshotStale(report, now)) return 'ابتدا گزارش را تازه‌سازی کن.';
  if (
    service.lastProbe?.status === 'running' ||
    report.services.some((item) => item.lastProbe?.status === 'running')
  )
    return 'آخرین گزارش، آزمون در حال انجام نشان می‌دهد؛ گزارش را تازه‌سازی کن.';
  const cooldown = aiMonitoringCooldownSeconds(service, report, now);
  if (cooldown > 0) return `برای آزمون بعدی ${cooldown.toLocaleString('fa-IR')} ثانیه صبر کن.`;
  if (!service.canProbe) return 'آزمون این سرویس در تنظیمات فعلی مجاز نیست.';
  return null;
}

/** Render only a hostname, never URLs, credentials, query strings or secret references. */
export function safeAiMonitoringHost(host: string | null): string {
  if (!host || !/^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?(?::[0-9]{1,5})?$/i.test(host))
    return 'نمایش داده نمی‌شود';
  return host;
}

export function aiMonitoringReasonText(code: string | null): string {
  const labels: Readonly<Record<string, string>> = {
    CONFIGURATION_REQUIRED: 'تنظیمات لازم کامل نیست.',
    PROVIDER_NOT_CONFIGURED: 'ارائه‌دهنده‌ای تنظیم نشده است.',
    INVALID_CONFIGURATION: 'تنظیمات سرویس معتبر نیست.',
    GLOBAL_STOP: 'توقف کلی هوش مصنوعی فعال است.',
    MONITOR_KEY_REQUIRED: 'تنظیم امن پایش کامل نیست؛ مسئول سامانه باید آن را بررسی کند.',
    PROVIDER_DISABLED: 'این ارائه‌دهنده غیرفعال است.',
    AMBIGUOUS_PROVIDER: 'مسیر ارائه‌دهنده یکتا نیست؛ تنظیمات باید بررسی شود.',
    MODEL_USAGE_UNSUPPORTED: 'مدل برای گزارش مصرف موردنیاز تنظیم نشده است.',
    MODEL_NOT_CONFIGURED: 'مدلی برای این مسیر تنظیم نشده است.',
    CREDENTIAL_REQUIRED: 'اطلاعات امن اتصال کامل نیست؛ مسئول سامانه باید آن را بررسی کند.',
    AUDIO_FIXTURE_REQUIRED: 'نمونهٔ صوت آزمایشی مجاز تنظیم نشده است.',
    LOCAL_FIXTURE_ONLY: 'آماده‌سازی دانش محلی است و اتصال خارجی را تأیید نمی‌کند.',
    NOT_PROBEABLE: 'این مسیر برای آزمون واقعی آماده نیست.',
    PROBE_IN_PROGRESS: 'یک آزمون هنوز در حال انجام است.',
    TIMEOUT: 'پاسخ در زمان مجاز نرسید؛ نتیجهٔ قطعی نداریم.',
    TRANSPORT_ERROR: 'نتیجهٔ ارتباط دریافت نشد و نامشخص است.',
    AUTH_REJECTED: 'ارائه‌دهنده اطلاعات اتصال را نپذیرفت.',
    RATE_LIMITED: 'ارائه‌دهنده تعداد درخواست‌ها را محدود کرده است.',
    UPSTREAM_UNAVAILABLE: 'ارائه‌دهنده در زمان آزمون پاسخ قابل استفاده نداد.',
    PROVIDER_ERROR: 'ارائه‌دهنده برای اجرای آزمون خطا گزارش کرده است.',
    HTTP_ERROR: 'ارائه‌دهنده پاسخ موفق نداد.',
    REDIRECT_REJECTED: 'ارائه‌دهنده درخواست تغییر مسیر داد و آزمون متوقف شد.',
    INVALID_RESPONSE: 'ساختار پاسخ ارائه‌دهنده معتبر نبود.',
    EXPECTED_RESPONSE_MISMATCH: 'محتوای پاسخ با آزمون مورد انتظار سازگار نبود.',
    RESPONSE_TOO_LARGE: 'پاسخ از حد مجاز آزمون بزرگ‌تر بود.',
    USAGE_MISSING: 'ارائه‌دهنده مصرف توکن را گزارش نکرده است.',
    USAGE_INVALID: 'مقدار مصرف گزارش‌شده معتبر نیست.',
    USAGE_PARTIAL: 'فقط بخشی از مصرف گزارش شده است.',
    PROBE_EXECUTION_UNKNOWN: 'نتیجهٔ اجرای آزمون مشخص نیست؛ گزارش را بررسی کن.',
    PROBE_COMPLETION_UNKNOWN: 'پایان آزمون تأیید نشده؛ نتیجه نامشخص است.',
    INVALID_PROBE_SNAPSHOT: 'اطلاعات ذخیره‌شدهٔ آزمون معتبر نیست؛ نتیجه قابل اتکا نیست.',
    TARGET_NOT_FOUND: 'مسیر این سرویس دیگر در تنظیمات پیدا نشد.',
    INVALID_PROBE_NONCE: 'شناسهٔ امن آزمون معتبر نبود؛ مسئول سامانه باید آن را بررسی کند.',
  };
  return code
    ? (labels[code] ?? 'علت با جزئیات قابل نمایش گزارش نشده است.')
    : 'علتی گزارش نشده است.';
}

export function aiMonitoringErrorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) return 'نشست اپراتور معتبر نیست؛ دوباره وارد کنسول شو.';
    if (error.status === 403) return 'دسترسی لازم برای این کار را نداری.';
    const reason = error.envelope.error.details?.find((detail) => detail.path === 'probe')?.code;
    const reasons: Readonly<Record<string, string>> = {
      PROBE_RATE_LIMITED:
        'سقف ۱۰ آزمون در یک ساعت پر شده است؛ پس از آزادشدن ظرفیت ساعتی دوباره تلاش کن.',
      PROBE_COOLDOWN: 'از آخرین آزمون باید دست‌کم ۶۰ ثانیه بگذرد؛ سپس گزارش را تازه‌سازی کن.',
      PROBE_IN_PROGRESS: 'آزمون دیگری هنوز در حال انجام است؛ گزارش را تازه‌سازی کن.',
      CONFIGURATION_CHANGED:
        'تنظیمات سرویس تغییر کرده است؛ گزارش را تازه‌سازی کن و مسیر جدید را بررسی کن.',
      IDEMPOTENCY_CONFLICT:
        'شناسهٔ این درخواست با آزمون دیگری تداخل دارد؛ پیش از آزمون تازه، گزارش را بررسی کن.',
      SERVICE_NOT_FOUND: 'این سرویس دیگر در گزارش فعلی وجود ندارد؛ گزارش را تازه‌سازی کن.',
      PROBE_NOT_AVAILABLE: 'آزمون این سرویس در تنظیمات فعلی مجاز نیست؛ گزارش را تازه‌سازی کن.',
    };
    if (reason && reasons[reason]) return reasons[reason];
    if (error.status === 409)
      return 'تنظیمات یا وضعیت آزمون تغییر کرده است؛ گزارش را تازه‌سازی کن.';
    if (error.status === 429)
      return 'تعداد درخواست‌های آزمون محدود شده است؛ گزارش را بررسی کن و بعداً دوباره تلاش کن.';
    if (error.status >= 400 && error.status < 500)
      return 'این درخواست پذیرفته نشد؛ گزارش و تنظیمات سرویس را بررسی کن.';
  }
  return 'دریافت نتیجه انجام نشد؛ وضعیت اتصال را بررسی کن و دوباره تلاش کن.';
}
